import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import AgentModel from './models/AgentModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import AgentApi, { DeleteAgentByIdParam } from './api/AgentApi';
import { AgentSearchSelector, setCheckedAgents, setDatatableSelectedAgentIndex, setOrderBy, setOrderDirection, setTriggerAgentSearch } from './reducers/AgentSearchReducer';
import { agentInputSelector, setSelectedAgentId } from './reducers/AgentInputReducer';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';

const AgentTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let agentData = useSelector(AgentSearchSelector);
    const AgentInputViewData = useSelector(agentInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    const columns = [
        {
            Field: Utility.propertyOf<AgentModel>("name"),
            Title: "Agent Name",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("country"),
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("portNames"),
            Title: "Ports handling",
            AllowSort: false,
            Render: (rowData: AgentModel) => {
                return <ul>
                    {
                        rowData &&
                        rowData.portNames.map((portName: string) => {
                            return <li>
                                {portName}
                            </li>
                        })
                    }
                </ul>
            }
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("cargoesHandling"),
            Title: "Cargoes handling",
            AllowSort: false,
            Render: (rowData: AgentModel) => {
                return <ul>
                    {
                        rowData &&
                        rowData.cargoesHandling.map((cargoHandling: CargoTypeModel) => {
                            return <li>
                                {cargoHandling.name}
                            </li>
                        })
                    }
                </ul>
            }
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("remark"),
            Title: "Remark",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("contactDetails"),
            Title: "Contact Details",
            AllowSort: true,
            Render: (rowData: AgentModel) => {
                let previewButton = rowData.contactDetails && rowData.contactDetails.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Button
                        className="tonnage-table-preview-full-tc"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.contactDetails && rowData.contactDetails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.contactDetails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.contactDetails || rowData.contactDetails.length === 0}
                    >
                        {previewButton}
                    </Button>
                </>
            }
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("userDisplayName"),
            Title: "Broker",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("updatedDate"),
            Title: "Updated Date",
            AllowSort: true,
            Render: (rowData: AgentModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<AgentModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: AgentModel) => (
                <>
                    {userProfile.userGroup === "Administrator" &&
                        <Tooltip title="Delete Agent">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteAgent(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<AgentModel>,
    ];

    const columnsForCopy = [
        {
            Field: Utility.propertyOf<AgentModel>("name"),
            Title: "Agent Name",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("country"),
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("portNames"),
            Title: "Port Open",
            AllowSort: true,
            Render: (rowData: AgentModel) => {
                return <ul>
                    {
                        rowData &&
                        rowData.portNames.map((portName: string) => {
                            return <li>
                                {portName}
                            </li>
                        })
                    }
                </ul>
            }
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("remark"),
            Title: "Remark",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>,
        {
            Field: Utility.propertyOf<AgentModel>("contactDetails"),
            Title: "Contact Details",
            AllowSort: true,
        } as DatatableColumnModel<AgentModel>];

    const deleteAgent = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                AgentApi.Delete({
                    id: id
                } as DeleteAgentByIdParam)
                    .then((response: BaseResponse<AgentModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Agent deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerAgentSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const formatUpdatedDate = (dateString: string): string => {
        if (!dateString) {
            return "";
        }
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "agent-datatable-search-result nice-scroll";

        if (!AgentInputViewData.agentInputExpand) {
            className += " agent-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "table-copy-hide";
                    }}
                >Copy contact details to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {agentData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<AgentModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={agentData.searchResults}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<AgentModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerAgentSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(tonnage: AgentModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedAgentId(tonnage.id))
                    dispatch(setDatatableSelectedAgentIndex(index));
                }
            }}
            selectedRowIndex={agentData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={agentData.query.orderBy}
            orderDirection={agentData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: AgentModel[]) => {
                dispatch(setCheckedAgents(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
        <div id="table-copy"
            className='table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<AgentModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={agentData.checkedAgents}
                useItemPerPage={false}
                columns={columnsForCopy}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default AgentTable;